import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import {
  DynamicHotelProps,
  GoToRoomDetailsParams,
  GroupedRooms,
  Hotel,
  HotelRoom,
  RoomClass,
} from 'ts/hotel/interfaces/HotelResponse';
import { groupRoomClasses, isRoomVisible } from 'utils/hotel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import RoomsFilter from '../../Details/RoomsFilter';
import { FilterRoom } from 'ts/hotel/classes/FilterRoom';
import HotelCardRoomItem from './HotelCardRoomItem';
import { MainContext } from 'contexts/MainContext';

interface HotelCardRoomsProps {
  rooms: RoomClass[];
  showRooms: boolean;
  hotel: Hotel;
  dynamic?: DynamicHotelProps;
  goToDetails: (params: GoToRoomDetailsParams) => void;
}

const HotelCardRooms: FC<HotelCardRoomsProps> = ({ goToDetails, ...props }) => {
  const [filterRoom, setFilterRoom] = useState(new FilterRoom());
  const [rooms, setRooms] = useState(props.rooms);
  const { userSiteConfig } = useContext(MainContext);
  const [groupedRooms, setGroupedRooms] = useState<GroupedRooms[]>([]);
  const roomRef = useRef<HTMLDivElement>(null);
  const [cancellationModalVisibleIndex, setCancellationModalVisibleIndex] = useState('');
  const [remarkModalVisibleIndex, setRemarkModalVisibleIndex] = useState('');
  const { user } = useContext(MainContext);

  const areRoomsGrouped = props.dynamic ? false : userSiteConfig.hotel.areRoomsGrouped;
  const areRoomsColoured = props.dynamic ? false : userSiteConfig.hotel.areRoomsColoured;
  const showRoomsInTab = props.dynamic ? false : userSiteConfig.hotel.showRoomsInTab;
  const defaultAreRoomsFiltered = props.dynamic ? false : userSiteConfig.hotel.areRoomsFiltered;
  const areRoomsFiltered =
    user?.branch?.hotelDisplaySupplierName || props.dynamic ? defaultAreRoomsFiltered : true;

  const visibleRoomClasses = props.rooms?.filter((rc) => isRoomVisible(rc, areRoomsFiltered));
  const hasImageSupplement = rooms
    .filter((room) => room.visible && room.visibleSm)
    .some((room) => room.hotelRooms[0].suppliments?.filter((sup) => sup.type === 'I')?.[0]?.desc);

  useEffect(() => {
    let newRooms = props.rooms;
    if (rooms.length === props.rooms.length) {
      for (let i = 0; i < props.rooms.length; i++) {
        newRooms[i].visibleSm = rooms[i].visibleSm;
      }
      setRooms(newRooms);
    } else {
      setRooms(props.rooms);
    }
  }, [props.rooms]); // eslint-disable-line

  useEffect(() => {
    const roomToGroup = areRoomsFiltered
      ? props.rooms.filter((rc) => rc.visibleFiltered)
      : props.rooms;
    if (areRoomsGrouped) {
      const newGroupedRooms = groupRoomClasses(roomToGroup);
      setGroupedRooms(newGroupedRooms);
    }
  }, [areRoomsGrouped, areRoomsFiltered, visibleRoomClasses.length]); // eslint-disable-line

  useEffect(() => {
    if (areRoomsColoured) {
      setRooms(
        rooms.map((room) => {
          return {
            ...room,
            hotelRooms: room.hotelRooms.map((hotelRoom) => {
              return {
                ...hotelRoom,
                displayName: setRoomColored(hotelRoom.displayName, hotelRoom),
              };
            }),
          };
        })
      );
    } else {
      const regex = /<span\s+style="color:\s*[^"]*">|<\/span>/g;
      setRooms(
        rooms.map((room) => {
          return {
            ...room,
            hotelRooms: room.hotelRooms.map((hotelRoom) => {
              const result = room.hotelRooms[0].displayName?.replace(regex, '');

              return {
                ...hotelRoom,
                displayName: result,
              };
            }),
          };
        })
      );
    }
  }, [areRoomsColoured]); // eslint-disable-line

  useEffect(() => {
    if (props.hotel.highlightedUk) {
      setTimeout(() => {
        if (roomRef.current) {
          const room = roomRef.current.querySelector('.hotel-card-room-highlighted') as HTMLElement;

          if (room) {
            roomRef.current.scrollTo({
              behavior: 'smooth',
              top:
                room.getBoundingClientRect().top -
                roomRef.current.getBoundingClientRect().top +
                roomRef.current.scrollTop,
            });
          }
        }
      }, 200);
    }
  }, [props.hotel.highlightedUk]); // eslint-disable-line

  const openGroup = (index: number) => {
    const newGroupedRooms = groupedRooms.map((groupedRoom, grIndex) => {
      return grIndex === index ? { ...groupedRoom, open: !groupedRoom.open } : groupedRoom;
    });
    setGroupedRooms(newGroupedRooms);
  };

  const setRoomColored = (str: string, hotelRoom: HotelRoom) => {
    if (areRoomsColoured) {
      let result = str;
      const wordsColors = [
        { word: hotelRoom.grp, color: 'maroon' },
        { word: hotelRoom.cat, color: 'darkgreen' },
        { word: hotelRoom.typ, color: 'green' },
        { word: hotelRoom.bdr, color: 'blue' },
        { word: hotelRoom.vew, color: 'darkgreen' },
        { word: hotelRoom.bed, color: 'purple' },
        { word: hotelRoom.ada, color: 'gold' },
        { word: hotelRoom.pax, color: 'darkblue' },
        { word: hotelRoom.brd, color: 'brown' },
      ];

      wordsColors.forEach((wordColor) => {
        if (wordColor.word) {
          wordColor.word
            .replace(',')
            .split(' ')
            .forEach((word: string) => {
              result = result.replace(new RegExp(word, 'gi'), (match) => {
                if (
                  '<span style="color:maroon">'.includes(match) ||
                  '<span style="color:darkgreen">'.includes(match) ||
                  '<span style="color:green">'.includes(match) ||
                  '<span style="color:blue">'.includes(match) ||
                  '<span style="color:purple">'.includes(match) ||
                  '<span style="color:gold">'.includes(match) ||
                  '<span style="color:brown">'.includes(match) ||
                  '<span style="color:darkblue">'.includes(match) ||
                  '<span class="highlight">'.includes(match) ||
                  '</span>'.includes(match)
                ) {
                  return match;
                }
                return `<span style="color:${wordColor.color}">${match}</span>`;
              });
            });
        }
      });

      return result;
    }

    return str;
  };

  const roomsFilterProps = {
    rooms: rooms.filter((room) => room.visibleFiltered || !areRoomsFiltered) as any,
    setRooms: setRooms as any,
    setFilterRoom,
    hotel: props.hotel,
  };

  return (
    <div className={'w100p ' + showRoomsInTab ? 'hotel-card-rooms-intab' : ''}>
      {props.showRooms && !areRoomsGrouped && (
        <div className="hotel-card-rooms-ungroup">
          <RoomsFilter {...roomsFilterProps} />
          <div className="hotel-card-rooms" ref={roomRef}>
            {rooms
              .filter(
                (room) =>
                  room.visible && room.visibleSm && (room.visibleFiltered || !areRoomsFiltered)
              )
              .map((room, index) => (
                <HotelCardRoomItem
                  key={index}
                  hasImageSupplement={hasImageSupplement}
                  room={room}
                  hotel={props.hotel}
                  highlightedUk={props.hotel.highlightedUk}
                  filterRoom={filterRoom}
                  goToDetails={goToDetails}
                  dynamic={props.dynamic}
                  cancellationModalVisibleIndex={cancellationModalVisibleIndex}
                  setCancellationModalVisibleIndex={setCancellationModalVisibleIndex}
                  remarkModalVisibleIndex={remarkModalVisibleIndex}
                  setRemarkModalVisibleIndex={setRemarkModalVisibleIndex}
                />
              ))}
          </div>
        </div>
      )}

      {props.showRooms && areRoomsGrouped && (
        <div className="hotel-card-rooms-group">
          <RoomsFilter
            {...roomsFilterProps}
            grRooms={groupedRooms}
            setGrRooms={setGroupedRooms}
          />
          <div className="hotel-card-rooms" ref={roomRef}>
            {groupedRooms
              .filter((groupedRoom) =>
                groupedRoom.data.some(
                  (grDataItem) =>
                    grDataItem.visible &&
                    grDataItem.visibleSm &&
                    (grDataItem.visibleFiltered || !areRoomsFiltered)
                )
              )
              .map((groupedRoom, index) => (
                <div
                  key={groupedRoom.name + index}
                  className={
                    'hotel-card-room-group' +
                    (groupedRoom.open ? ' hotel-card-room-group-active' : '')
                  }
                >
                  {groupedRoom.data
                    .filter((room) => room.visible && room.visibleSm)
                    .filter((_room, roomIndex) => roomIndex === 0 || groupedRoom.open)
                    .map((room, roomIndex) => (
                      <HotelCardRoomItem
                        key={roomIndex}
                        room={room}
                        hotel={props.hotel}
                        highlightedUk={props.hotel.highlightedUk}
                        grouped={true}
                        hasImageSupplement={hasImageSupplement}
                        extraClassName={roomIndex !== 0 ? 'hotel-card-room-sub' : ''}
                        filterRoom={filterRoom}
                        goToDetails={goToDetails}
                        dynamic={props.dynamic}
                        cancellationModalVisibleIndex={cancellationModalVisibleIndex}
                        setCancellationModalVisibleIndex={setCancellationModalVisibleIndex}
                        remarkModalVisibleIndex={remarkModalVisibleIndex}
                        setRemarkModalVisibleIndex={setRemarkModalVisibleIndex}
                        cancellationModalKey={index + '/' + groupedRoom.index}
                        showRemarkModal={false}
                        groupToggle={
                          <div className="mr-5">
                            {groupedRoom.data.filter((rc) => isRoomVisible(rc, areRoomsFiltered))
                              .length > 1 && roomIndex === 0 ? (
                              <FontAwesomeIcon
                                className="cur-poi room-group-icon"
                                icon={groupedRoom.open ? faChevronUp : faChevronDown}
                                onClick={() => openGroup(groupedRoom.index)}
                              />
                            ) : (
                              <span></span>
                            )}
                          </div>
                        }
                      />
                    ))}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HotelCardRooms;
